import { useEffect, useRef, useState } from "react";
import { useClerk } from "@clerk/clerk-react";
import { getItem as safeGetItem, setItem as safeSetItem } from "../utils/safeStorage";

const IDLE_LIMIT_MS = 60 * 60 * 1000;      // 60 minutes -> sign out
const WARNING_AT_MS = 59 * 60 * 1000;      // 59 minutes -> show warning
const ACTIVITY_THROTTLE_MS = 5 * 1000;     // write to storage at most every 5s
const POLL_MS = 1000;                      // check idle state every second

const STORAGE_KEY = "dupont_last_activity";
const LOGOUT_KEY = "dupont_idle_logout";
// "wheel", and listening in the capture phase: the pages scroll inside their
// own box, not the window, and a scroll there never reaches the window on its
// own. Before this, someone reading a long page by scrolling alone counted as
// idle and could be signed out mid-read.
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "wheel", "touchstart"];
const LISTEN = { passive: true, capture: true };

function readLastActivity() {
  const raw = safeGetItem(STORAGE_KEY);
  const parsed = raw ? parseInt(raw, 10) : NaN;
  return Number.isFinite(parsed) ? parsed : Date.now();
}

function writeLastActivity(ts) {
  safeSetItem(STORAGE_KEY, String(ts));
}

export default function useIdleTimeout(enabled) {
  const { signOut } = useClerk();
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const lastWriteRef = useRef(0);
  const signedOutRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      setShowWarning(false);
      signedOutRef.current = false;
      return undefined;
    }

    writeLastActivity(Date.now());
    lastWriteRef.current = Date.now();

    const markActive = () => {
      const now = Date.now();
      if (now - lastWriteRef.current < ACTIVITY_THROTTLE_MS) return;
      lastWriteRef.current = now;
      writeLastActivity(now);
    };

    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, markActive, LISTEN)
    );

    const doSignOut = (broadcast) => {
      if (signedOutRef.current) return;
      signedOutRef.current = true;
      if (broadcast) {
        safeSetItem(LOGOUT_KEY, String(Date.now()));
      }
      signOut();
    };

    const onStorage = (e) => {
      if (e.key === LOGOUT_KEY && e.newValue) {
        doSignOut(false);
      }
    };
    window.addEventListener("storage", onStorage);

    const interval = setInterval(() => {
      const idleFor = Date.now() - readLastActivity();

      if (idleFor >= IDLE_LIMIT_MS) {
        doSignOut(true);
        return;
      }

      if (idleFor >= WARNING_AT_MS) {
        setShowWarning(true);
        setSecondsLeft(Math.max(0, Math.ceil((IDLE_LIMIT_MS - idleFor) / 1000)));
      } else {
        setShowWarning(false);
      }
    }, POLL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, markActive, LISTEN));
      window.removeEventListener("storage", onStorage);
      clearInterval(interval);
    };
  }, [enabled, signOut]);

  const stayActive = () => {
    const now = Date.now();
    writeLastActivity(now);
    lastWriteRef.current = now;
    setShowWarning(false);
  };

  return { showWarning, secondsLeft, stayActive };
}
